import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

// LogicLine brand + supporting tints — keeps the burst on-brand, not generic.
const COLORS = ["#00599B", "#F68121", "#4A90C2", "#F6A65B", "#5E8B5F"];

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * A one-shot confetti burst — a fixed, click-through canvas overlay that emits a
 * radial spray from the upper-middle of the viewport and fades out after
 * `durationMs`, then unmounts itself via `onDone`. Self-contained (no library).
 *
 * Honors `prefers-reduced-motion`: users who asked the OS to reduce motion get
 * NO animation — the effect resolves immediately so the surrounding thank-you
 * text still shows. Purely decorative, so `pointer-events: none` + aria-hidden.
 */
export function Confetti({
  durationMs = 1600,
  count = 130,
  onDone,
}: {
  durationMs?: number;
  count?: number;
  onDone?: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    if (prefersReducedMotion()) {
      onDoneRef.current?.();
      return;
    }
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = vw * dpr;
    canvas.height = vh * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const cx = vw / 2;
    const cy = vh * 0.32;
    const parts = Array.from({ length: count }, (_, i) => {
      const angle = (Math.PI * 2 * i) / count + Math.random() * 0.6;
      const speed = 6 + Math.random() * 7;
      return {
        x: cx,
        y: cy,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 4,
        size: 5 + Math.random() * 6,
        color: COLORS[i % COLORS.length]!,
        rot: Math.random() * Math.PI,
        vr: (Math.random() - 0.5) * 0.3,
      };
    });

    const gravity = 0.28;
    const drag = 0.995;
    let raf = 0;
    let start = 0;

    function frame(ts: number) {
      if (!start) start = ts;
      const elapsed = ts - start;
      const progress = elapsed / durationMs;
      const fade = progress > 0.7 ? Math.max(0, 1 - (progress - 0.7) / 0.3) : 1;
      ctx!.clearRect(0, 0, vw, vh);
      for (const p of parts) {
        p.vx *= drag;
        p.vy = p.vy * drag + gravity;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;
        ctx!.save();
        ctx!.globalAlpha = fade;
        ctx!.translate(p.x, p.y);
        ctx!.rotate(p.rot);
        ctx!.fillStyle = p.color;
        ctx!.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
        ctx!.restore();
      }
      if (elapsed < durationMs) {
        raf = requestAnimationFrame(frame);
      } else {
        ctx!.clearRect(0, 0, vw, vh);
        onDoneRef.current?.();
      }
    }
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [durationMs, count]);

  if (typeof document === "undefined") return null;
  // Portal to <body> so the fixed, full-viewport canvas escapes any transformed
  // ancestor (e.g. the submit modal), which would otherwise trap + clip it.
  return createPortal(
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100vh",
        pointerEvents: "none",
        zIndex: 9999,
      }}
    />,
    document.body,
  );
}
